/**
 * Screen ↔ model-plane ↔ disk mappings shared by the renderers, the overlay and input.
 * All screen coordinates are CSS pixels with y pointing down.
 */
import type { Complex } from '../geometry/complex';
import { type ModelId, toDisk } from '../geometry/models';

export interface Camera {
  /** Model-plane point at the centre of the view. */
  cx: number;
  cy: number;
  /** Model units per CSS pixel. */
  scale: number;
  width: number;
  height: number;
}

/**
 * Framing for each model. The disk and Klein disk fill the view with radius `radiusPx`. The
 * half-plane rests on its real axis near the bottom edge and the band runs across the view; both
 * are zoomed out so their infinite extent shows. `morph` (0 = the disk picture, 1 = the model)
 * interpolates from the disk framing, so model transitions glide instead of jumping. The
 * half-plane family draws the disk centred at i, so its starting camera is centred there.
 */
export function cameraFor(
  model: ModelId,
  width: number,
  height: number,
  radiusPx: number,
  morph = 1,
): Camera {
  const s0 = 1 / radiusPx;
  const e = Math.max(0, Math.min(1, morph));
  const logLerp = (s1: number) => Math.exp(Math.log(s0) + (Math.log(s1) - Math.log(s0)) * e);
  if (model === 'halfplane') {
    const s1 = 1.4 / radiusPx;
    const bottom = Math.min(18, 0.03 * height);
    const cy1 = (height / 2 - bottom) * s1;
    return { cx: 0, cy: 1 + (cy1 - 1) * e, scale: logLerp(s1), width, height };
  }
  if (model === 'band') return { cx: 0, cy: 0, scale: logLerp(1.3 / radiusPx), width, height };
  return { cx: 0, cy: 0, scale: s0, width, height };
}

/** Zoomed camera framing a disk-coordinate box (for the domain editor). */
export function cameraForBox(
  box: { x0: number; y0: number; x1: number; y1: number },
  width: number,
  height: number,
  fill = 0.82,
): Camera {
  const bw = box.x1 - box.x0;
  const bh = box.y1 - box.y0;
  const scale = Math.max(bw / (width * fill), bh / (height * fill));
  return { cx: (box.x0 + box.x1) / 2, cy: (box.y0 + box.y1) / 2, scale, width, height };
}

export const screenToModel = (cam: Camera, x: number, y: number): Complex => ({
  re: cam.cx + (x - cam.width / 2) * cam.scale,
  im: cam.cy + (cam.height / 2 - y) * cam.scale,
});

export const modelToScreen = (cam: Camera, w: Complex): { x: number; y: number } => ({
  x: cam.width / 2 + (w.re - cam.cx) / cam.scale,
  y: cam.height / 2 - (w.im - cam.cy) / cam.scale,
});

/** Screen → disk (null outside the plane). */
export function screenToDisk(
  cam: Camera,
  model: ModelId,
  x: number,
  y: number,
  morph = 1,
): Complex | null {
  return toDisk(model, screenToModel(cam, x, y), morph);
}

/**
 * Screen → disk for dragging: points outside the plane are pulled back inside, and points very
 * close to the boundary are clamped radially, so a drag that slips past the edge does not fling
 * the view across infinite distances.
 */
export function screenToDiskClamped(
  cam: Camera,
  model: ModelId,
  x: number,
  y: number,
  maxRadius = 0.9,
): Complex {
  let m = screenToModel(cam, x, y);
  if (model === 'halfplane' && m.im <= 1e-6) m = { re: m.re, im: 1e-6 };
  if (model === 'band') m = { re: m.re, im: Math.max(-0.999, Math.min(0.999, m.im)) };
  if (model === 'klein' || model === 'disk') {
    const r = Math.hypot(m.re, m.im);
    if (r >= 0.9999) m = { re: (m.re / r) * 0.9999, im: (m.im / r) * 0.9999 };
  }
  const z = toDisk(model, m) ?? { re: 0, im: 0 };
  const r = Math.hypot(z.re, z.im);
  return r > maxRadius ? { re: (z.re / r) * maxRadius, im: (z.im / r) * maxRadius } : z;
}
